// Scene 1 — Wake up. Miko introduces the empty stand. Three lines of dialogue,
// then two choices: "Yes" or "Tell me more".
// "Yes" → StartBusiness('smoothieStand') + CompleteWakeUp, advance.

import { useState } from 'react';
import { dayKeyFromDate } from '@biz-kid/engine';
import { useDispatch, useGameState } from '@biz-kid/store';
import { Card, Eyebrow } from '../../ui/kit.js';
import { StoryShell, Coach, StoryChoice, choiceGrid } from '../storyKit.js';

export type IslandWakeUpProps = { onComplete: () => void };

// Optional "how it works" — only shown if the kid taps for it. The default path
// is one tap to open the stand and start playing.
const TELL_ME_MORE_LINES = [
  'Each drop costs B$3 to make. Charge more than that and the rest is yours.',
  'Charge too much and people walk past; too little and you barely make a thing. Find the sweet spot — that’s the game.',
];

export function IslandWakeUp({ onComplete }: IslandWakeUpProps) {
  const dispatch = useDispatch();
  const businessAlreadyStarted = useGameState((s) => s.businesses.length > 0);
  const [stage, setStage] = useState<'choosing' | 'tellMore'>('choosing');

  const start = () => {
    if (!businessAlreadyStarted) {
      const err = dispatch({ kind: 'StartBusiness', defId: 'smoothieStand' });
      if (err) {
        // Eg. INSUFFICIENT_CASH — log and continue to the next scene anyway.
        console.warn('StartBusiness failed:', err);
      }
    }
    dispatch({ kind: 'CompleteWakeUp', dayKey: dayKeyFromDate(new Date()) });
    onComplete();
  };

  return (
    <StoryShell eyebrow="Day 1 · The Empty Stand" backdrop="voxel-sky">
      {stage === 'tellMore' ? (
        <Coach key="more" lines={TELL_ME_MORE_LINES} onDone={() => setStage('choosing')} />
      ) : (
        <>
          <Card>
            <Eyebrow color="var(--brand)">Your store</Eyebrow>
            <p style={{ margin: '6px 0 0', fontSize: 16, color: 'var(--text)', lineHeight: 1.55 }}>
              Your Drop Store is empty — you&rsquo;ve got <b>B$180</b> to stock your first drop and go live. Ready?
            </p>
          </Card>
          <div style={choiceGrid}>
            <StoryChoice tone="fair" label="Yes — open up!" subtitle="Start selling right now." onClick={start} />
            <StoryChoice tone="neutral" label="How does it work?" subtitle="Quick 2-line explainer." onClick={() => setStage('tellMore')} />
          </div>
        </>
      )}
    </StoryShell>
  );
}
